
import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useForm } from 'react-hook-form';

import {
  getLDGSettings,
  getOptionList,
  getICAOList,
  getTKOFCalculation,
  getLDGOptionList,
  getLDGCalculation,
  getLDGMaxMin,
  getTKOFMaxMin,
} from '../utilities/perfCalcHelperFunctions';
import {
  getMETAR,
  getTAFV2,
  getTrainingWXFromAllData,
  parseTrainingWX,
} from '../utilities/helperfunctions';
import {
  altimInHgToQNH,
  calculateLandingWeight,
  formatTafToHTML,
  getTkofSettingsByIcao,
  tkofToRwyData,
  isTrainingMode,
  formatMetarToWindArray,
} from '../helpers';
import '../style/perf-calc.scss';

// Parse training METAR string into structured object
// Example: "xx1350 30011KT 9999 FEW040 08/02 Q1017 NOSIG"
const parseTrainingMetarToObject = (metarString) => {
  if (!metarString) return null;

  const result = {
    raw_text: metarString,
    wind_dir_degrees: null,
    wind_speed_kt: null,
    temp_c: null,
    altim_in_hg: null,
  };

  const windData = formatMetarToWindArray(metarString, true);
  if (windData.length > 0) {
    const wind = windData[0];
    result.wind_dir_degrees = wind.windDegrees === 'VRB' ? 0 : wind.windDegrees;
    result.wind_speed_kt = wind.windSpeed;
  }

  const tempMatch = metarString.match(/\s(M?\d{2})\/(M?\d{2})\s/);
  if (tempMatch) {
    let temp = tempMatch[1];
    if (temp.startsWith('M')) {
      result.temp_c = -parseInt(temp.substring(1), 10);
    } else {
      result.temp_c = parseInt(temp, 10);
    }
  }

  const qnhMatch = metarString.match(/Q(\d{4})/);
  if (qnhMatch) {
    const qnh = parseInt(qnhMatch[1], 10);
    result.altim_in_hg = (qnh * 0.02953).toFixed(2);
  }

  const altimMatch = metarString.match(/A(\d{4})/);
  if (altimMatch) {
    result.altim_in_hg = (parseInt(altimMatch[1], 10) / 100).toFixed(2);
  }

  return result;
};

export function PerfCalc(props) {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
    setValue,
  } = useForm();

  const [calculationResult, setCalculationResult] = useState('');
  const [openResultPopup, setOpenResultPopup] = useState(false);

  // state to determine what settings to show and calculate for(takeoff or landing)
  const [tkofMode, setTkofMode] = useState(true);
  const toggleCalcMode = () => {
    setTkofMode((prevState) => !prevState);
  };

  // State for the takeoff settings
  const [tkofSettings, setTkofSettings] = useState(null);
  const [depAirportICAO, setDepAirportICAO] = useState(null);
  const [tkofDepth, setTkofDepth] = useState('');
  const [tkofMetar, setTkofMetar] = useState();

  // State for the landing settings
  const [ldgSettings, setLdgSettings] = useState(null);
  const [destAirportICAO, setDestAirportICAO] = useState(null);
  const [ldgWindDir, setLdgWindDir] = useState('');
  const [ldgWindSpeed, setLdgWindSpeed] = useState('');
  const [ldgOAT, setLdgOAT] = useState('');
  const [ldgQNH, setLdgQNH] = useState('');
  const [ldgLW, setLdgLW] = useState('');
  const [ldgThrSpeedInc, setLdgThrSpeedInc] = useState(5);
  const [ldgAppClbGrad, setLdgAppClbGrad] = useState(2.5);
  const [ldgMetar, setLdgMetar] = useState();
  const [ldgTaf, setLdgTaf] = useState();

  const resetNumberInputs = () => {
    setValue('OAT', '');
    setValue('WindDir', '');
    setValue('WindSpeed', '');
    setValue('QNH', '');
    setTkofDepth('');
    setLdgWindDir('');
    setLdgWindSpeed('');
    setLdgOAT('');
    setLdgQNH('');
    setLdgLW('');
    setLdgThrSpeedInc(5);
    setLdgAppClbGrad(2.5);
  };

  // reset form and loads in new settings and airports when flight is changed (or on first render)
  useEffect(() => {
    resetNumberInputs();
    reset();
    setTkofSettings(props.tkofSettings);
    getLDGSettings(props.data?.sACReg || '', setLdgSettings, props.parseString);
    setDepAirportICAO(props.data?.sDepICAO?.[0]);
    setDestAirportICAO(props.data?.sDestICAO?.[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data, props.empNumber]);

  const handleTkofCalc = (formData) => {
    formData.FC = 20; // Hard code FC value due to business requirements
    getTKOFCalculation(
      formData,
      props.data,
      props.empNumber,
      props.parseString,
      setCalculationResult
    );
    setOpenResultPopup(true);
  };

  const handleLdgCalc = (formData) => {
    formData.FC = 20; // Hard code FC value due to business requirements
    getLDGCalculation(
      formData,
      props.data,
      props.empNumber,
      props.parseString,
      setCalculationResult
    );
    setOpenResultPopup(true);
  };

  useEffect(() => {
    const LW = calculateLandingWeight(props.data, props.reqFuel);
    setLdgLW(LW);
  }, [props.data, props.reqFuel]);

  // H�mta METAR och TAF data f�r LDG
  useEffect(() => {
    const fetchData = async () => {
      const icaoList = getICAOList(ldgSettings);
      // Invalid ICAO
      if (!icaoList.includes(destAirportICAO)) {
        setLdgMetar(undefined);
        setLdgTaf(undefined);
        return;
      }

      // Check for training mode first
      if (isTrainingMode()) {
        const trainingWX = getTrainingWXFromAllData(props.data, destAirportICAO);
        if (trainingWX?.sWX) {
          const parsed = parseTrainingWX(trainingWX.sWX);
          const metarData = parseTrainingMetarToObject(parsed.metar);
          setLdgMetar(metarData || undefined);
          setLdgTaf(parsed.taf ? { rawTAF: parsed.taf } : undefined);
          return;
        }
      }

      // Normal mode - fetch from API
      if (navigator.onLine) {
      const metar = await getMETAR(destAirportICAO);
      const taf = await getTAFV2(destAirportICAO);
      setLdgMetar(metar?.[0] || undefined);
      setLdgTaf(taf?.[0] || undefined);
      }
    };
    if (destAirportICAO && ldgSettings) {
      fetchData();
    }
  }, [destAirportICAO, ldgSettings, props.data]);

  // TKOF METAR
  useEffect(() => {
    const fetchData = async () => {
      const icaoList = getICAOList(tkofSettings);

      // Invalid ICAO
      if (!icaoList.includes(depAirportICAO)) {
        setTkofMetar(undefined);
        setValue('OAT', '');
        setValue('WindDir', '');
        setValue('WindSpeed', '');
        setValue('QNH', '');
        return;
      }

      let metarData = null;

      // Check for training mode first
      if (isTrainingMode()) {
        const trainingWX = getTrainingWXFromAllData(props.data, depAirportICAO);
        if (trainingWX?.sWX) {
          const parsed = parseTrainingWX(trainingWX.sWX);
          metarData = parseTrainingMetarToObject(parsed.metar);
        }
      }

      // Normal mode - fetch from API
      if (!metarData && navigator.onLine) {
      const metar = await getMETAR(depAirportICAO);
        metarData = metar?.[0];
      }

      setTkofMetar(metarData ?? undefined);

      const windDir = metarData?.wind_dir_degrees ?? '';
      const windSpeed = metarData?.wind_speed_kt ?? '';
      const QNH = metarData?.altim_in_hg
        ? altimInHgToQNH(metarData.altim_in_hg)
        : '';
      const OAT = metarData?.temp_c ? Math.trunc(Number(metarData.temp_c)) : '';
      setValue('OAT', OAT);
      setValue('WindDir', windDir);
      setValue('WindSpeed', windSpeed);
      setValue('QNH', QNH);
    };
    if (depAirportICAO && tkofSettings) {
      fetchData();
    }
  }, [depAirportICAO, tkofSettings, setValue, props.data]);

  // populates the list of runways for a given icao
  const populateRWYOptions = (selectedIcao, settings, form) => {
    const tkofData = getTkofSettingsByIcao(
      selectedIcao,
      settings.ADList[0]?.string
    );
    let rwyDataArray = tkofData ? tkofToRwyData(tkofData, false) : [];

    // // Remove the first because it's not a runway
    if (form === 'TKOF') rwyDataArray = rwyDataArray.slice(1);

    return rwyDataArray.map((rwyData, index) => {
      return (
        <option key={'RWY' + index} value={rwyData.rwy}>
          {rwyData.rwy}
        </option>
      );
    });
  };

  const optionListComponent = (
    setting,
    tagName,
    displayName,
    registerName,
    getOptionListFuncton
  ) => {
    return (
      <>
        <div className='input-row'>
          <label htmlFor={registerName}>{displayName}</label>
          <select
            {...register(registerName, { required: 'This field is required' })}
          >
            {getOptionListFuncton(setting, tagName).map((option, index) => {
              return (
                <option
                  key={registerName + 'Option' + index}
                  value={option?.m_sOption[0]}
                >
                  {option?.m_sOption[0]}
                </option>
              );
            })}
          </select>
        </div>
      </>
    );
  };

  const tkofForm = () => {
    if (!tkofSettings) return <p> Loading Settings... </p>;

    if (depAirportICAO === null || depAirportICAO === undefined)
      return <p> Loading Settings... </p>;

    // If tkofSettings is not an object, it will be a string as there will have
    // an error in the fetch to the webservice of some kind or the given flight does not
    // have any settings attached to it
    if (typeof tkofSettings !== 'object') return tkofSettings;

    function handleICAOChange(e) {
      setDepAirportICAO(e.target.value.toUpperCase());
      populateRWYOptions(depAirportICAO, tkofSettings, 'TKOF');
    }

    // getting all max and min values for different settings
    const tkofWindDirMaxMin = getTKOFMaxMin(tkofSettings, 'WindDir', false);
    const tkofWindSpeedMaxMin = getTKOFMaxMin(tkofSettings, 'WindSpeed', false);
    const tkofOATMaxMin = getTKOFMaxMin(tkofSettings, 'OAT', false);
    const tkofQNHMaxMin = getTKOFMaxMin(tkofSettings, 'QNH', false);
    const tkofStWaterDepthMaxMin = getTKOFMaxMin(
      tkofSettings,
      'ST WATER DEPTH',
      true
    );
    const tkofSlushDepthMaxMin = getTKOFMaxMin(
      tkofSettings,
      'SLUSH DEPTH',
      true
    );
    const tkofDrySnowDepthMaxMin = getTKOFMaxMin(
      tkofSettings,
      'DRY SNOW DEPTH',
      true
    );
    const tkofWetSnowDepthMaxMin = getTKOFMaxMin(
      tkofSettings,
      'WET SNOW DEPTH',
      true
    );

    return (
      <>
        <form onSubmit={handleSubmit(handleTkofCalc)} className='form'>
          <div className='input-row'>
            <span>ACReg: </span>
            <span>{props.data?.sACReg}</span>
          </div>

          <div className='input-row'>
            <label htmlFor='ICAO'>ICAO:</label>
            <input
              {...register('ICAO', {
                required: 'This field is required',
                minLength: {
                  value: 4,
                  message: 'ICAO code is 4 letters',
                },
                pattern: {
                  value: /^[A-Z]+$/i,
                  message: 'Invalid ICAO code',
                },
              })}
              list='icao'
              style={{ textTransform: 'uppercase' }}
              maxLength={4}
              value={depAirportICAO}
              onChange={(e) => {
                handleICAOChange(e);
                reset();
              }}
            />
            <datalist id='icao'>
              {getICAOList(tkofSettings).map((icao, index) => {
                return (
                  <option key={'ICAO' + index} value={icao}>
                    {icao}
                  </option>
                );
              })}
            </datalist>
          </div>
          {errors.ICAO && <p className='error_text'>{errors.ICAO?.message}</p>}

          <div className='input-row'>
            <label htmlFor='RWY'>RWY:</label>
            <select
              {...register('RWY', { required: 'This field is required' })}
            >
              {populateRWYOptions(depAirportICAO, tkofSettings, 'TKOF')}
            </select>
          </div>
          {errors.RWY && <p className='error_text'>{errors.RWY?.message}</p>}

          {optionListComponent(
            tkofSettings,
            'AntiSkid',
            'ANTISKID:',
            'AntiSkid',
            getOptionList
          )}
          {errors.AntiSkid && (
            <p className='error_text'>{errors.AntiSkid?.message}</p>
          )}

          {optionListComponent(
            tkofSettings,
            'ThrRev',
            'THR REV:',
            'ThrRev',
            getOptionList
          )}
          {errors.ThrRev && (
            <p className='error_text'>{errors.ThrRev?.message}</p>
          )}

          {optionListComponent(
            tkofSettings,
            'LandGear',
            'LAND GEAR:',
            'LandGear',
            getOptionList
          )}
          {errors.LandGear && (
            <p className='error_text'>{errors.LandGear?.message}</p>
          )}

          {optionListComponent(
            tkofSettings,
            'PowerManSys',
            'PWR MAN SYS:',
            'PowerManSys',
            getOptionList
          )}
          {errors.PowerManSys && (
            <p className='error_text'>{errors.PowerManSys?.message}</p>
          )}

          {optionListComponent(
            tkofSettings,
            'RWYCond',
            'RWY COND:',
            'RWYCond',
            getOptionList
          )}
          {errors.RWYCond && (
            <p className='error_text'>{errors.RWYCond?.message}</p>
          )}

          {watch('RWYCond') === 'ST WATER' && (
            <>
              <div className='input-row'>
                <label htmlFor='Depth'>
                  ST WATER DEPTH:
                  <span
                    style={{
                      fontSize: '0.65rem',
                      color: '#D81939',
                      textAlign: 'center',
                    }}
                  >
                    {' '}
                    ({tkofStWaterDepthMaxMin.min} - {tkofStWaterDepthMaxMin.max}
                    )
                  </span>
                </label>
                <input
                  {...register('Depth', {
                    required: 'This field is required',
                    min: {
                      value: tkofStWaterDepthMaxMin.min,
                      message: 'Minimum ' + tkofStWaterDepthMaxMin.min,
                    },
                    max: {
                      value: tkofStWaterDepthMaxMin.max,
                      message: 'Maximum ' + tkofStWaterDepthMaxMin.max,
                    },
                  })}
                  value={tkofDepth}
                  onChange={(e) => setTkofDepth(e.target.value)}
                  type='number'
                />
              </div>
              {errors.Depth && (
                <p className='error_text'>{errors.Depth?.message}</p>
              )}
            </>
          )}

          {watch('RWYCond') === 'DEGRADED BRAKING ACTION' && (
            <>
              {optionListComponent(
                tkofSettings,
                'BA',
                'BA:',
                'BA',
                getOptionList
              )}
              {errors.BA && <p className='error_text'>{errors.BA?.message}</p>}

            </>
          )}

          {watch('RWYCond') === 'SLUSH' && (
            <>
              <div className='input-row'>
                <label htmlFor='Depth'>
                  SLUSH DEPTH:
                  <span
                    style={{
                      fontSize: '0.65rem',
                      color: '#D81939',
                      textAlign: 'center',
                    }}
                  >
                    {' '}
                    ({tkofSlushDepthMaxMin.min} - {tkofSlushDepthMaxMin.max})
                  </span>
                </label>
                <input
                  {...register('Depth', {
                    required: 'This field is required',
                    min: {
                      value: tkofSlushDepthMaxMin.min,
                      message: 'Minimum ' + tkofSlushDepthMaxMin.min,
                    },
                    max: {
                      value: tkofSlushDepthMaxMin.max,
                      message: 'Maximum ' + tkofSlushDepthMaxMin.max,
                    },
                  })}
                  value={tkofDepth}
                  onChange={(e) => setTkofDepth(e.target.value)}
                  type='number'
                />
              </div>
              {errors.Depth && (
                <p className='error_text'>{errors.Depth?.message}</p>
              )}

              {optionListComponent(
                tkofSettings,
                'BA',
                'BA:',
                'BA',
                getOptionList
              )}
              {errors.BA && <p className='error_text'>{errors.BA?.message}</p>}

            </>
          )}

          {watch('RWYCond') === 'DRY SNOW' && (
            <>
              <div className='input-row'>
                <label htmlFor='Depth'>
                  DRY SNOW DEPTH:
                  <span
                    style={{
                      fontSize: '0.65rem',
                      color: '#D81939',
                      textAlign: 'center',
                    }}
                  >
                    {' '}
                    ({tkofDrySnowDepthMaxMin.min} - {tkofDrySnowDepthMaxMin.max}
                    )
                  </span>
                </label>
                <input
                  {...register('Depth', {
                    required: 'This field is required',
                    min: {
                      value: tkofDrySnowDepthMaxMin.min,
                      message: 'Minimum ' + tkofDrySnowDepthMaxMin.min,
                    },
                    max: {
                      value: tkofDrySnowDepthMaxMin.max,
                      message: 'Maximum ' + tkofDrySnowDepthMaxMin.max,
                    },
                  })}
                  value={tkofDepth}
                  onChange={(e) => setTkofDepth(e.target.value)}
                  type='number'
                />
              </div>
              {errors.Depth && (
                <p className='error_text'>{errors.Depth?.message}</p>
              )}

              {optionListComponent(
                tkofSettings,
                'BA',
                'BA:',
                'BA',
                getOptionList
              )}
              {errors.BA && <p className='error_text'>{errors.BA?.message}</p>}

            </>
          )}

          {watch('RWYCond') === 'WET SNOW' && (
            <>
              <div className='input-row'>
                <label htmlFor='Depth'>
                  WET SNOW DEPTH:
                  <span
                    style={{
                      fontSize: '0.65rem',
                      color: '#D81939',
                      textAlign: 'center',
                    }}
                  >
                    {' '}
                    ({tkofWetSnowDepthMaxMin.min} - {tkofWetSnowDepthMaxMin.max}
                    )
                  </span>
                </label>
                <input
                  {...register('Depth', {
                    required: 'This field is required',
                    min: {
                      value: tkofWetSnowDepthMaxMin.min,
                      message: 'Minimum ' + tkofWetSnowDepthMaxMin.min,
                    },
                    max: {
                      value: tkofWetSnowDepthMaxMin.max,
                      message: 'Maximum ' + tkofWetSnowDepthMaxMin.max,
                    },
                  })}
                  value={tkofDepth}
                  onChange={(e) => setTkofDepth(e.target.value)}
                  type='number'
                />
              </div>
              {errors.Depth && (
                <p className='error_text'>{errors.Depth?.message}</p>
              )}

              {optionListComponent(
                tkofSettings,
                'BA',
                'BA:',
                'BA',
                getOptionList
              )}
              {errors.BA && <p className='error_text'>{errors.BA?.message}</p>}

            </>
          )}

          <br></br>

          <p className='metar_taf_title'>METAR</p>
          <p className='metar_taf_raw_text'>
            {tkofMetar?.raw_text ?? 'METAR data not found'}
          </p>

          <br></br>

          <div className='input-row'>
            <label htmlFor='WindDir'>
              WIND DIR:
              <span
                style={{
                  fontSize: '0.65rem',
                  color: '#D81939',
                  textAlign: 'center',
                }}
              >
                {' '}
                ({tkofWindDirMaxMin.min} - {tkofWindDirMaxMin.max})
              </span>
            </label>
            <input
              {...register('WindDir', {
                required: 'This field is required',
                min: {
                  value: tkofWindDirMaxMin.min,
                  message: 'Minimum ' + tkofWindDirMaxMin.min,
                },
                max: {
                  value: tkofWindDirMaxMin.max,
                  message: 'Maximum ' + tkofWindDirMaxMin.max,
                },
              })}
              type='number'
            />
          </div>
          {errors.WindDir && (
            <p className='error_text'>{errors.WindDir?.message}</p>
          )}

          <div className='input-row'>
            <label htmlFor='WindSpeed'>
              WIND SPD:
              <span
                style={{
                  fontSize: '0.65rem',
                  color: '#D81939',
                  textAlign: 'center',
                }}
              >
                {' '}
                ({tkofWindSpeedMaxMin.min} - {tkofWindSpeedMaxMin.max})
              </span>
            </label>
            <input
              {...register('WindSpeed', {
                required: 'This field is required',
                min: {
                  value: tkofWindSpeedMaxMin.min,
                  message: 'Minimum ' + tkofWindSpeedMaxMin.min,
                },
                max: {
                  value: tkofWindSpeedMaxMin.max,
                  message: 'Maximum ' + tkofWindSpeedMaxMin.max,
                },
                pattern: {
                  value: /^-?\d+$/,
                  message: 'Not a valid input',
                },
              })}
              type='number'
            />
          </div>
          {errors.WindSpeed && (
            <p className='error_text'>{errors.WindSpeed?.message}</p>
          )}

          <div className='input-row'>
            <label htmlFor='OAT'>
              OAT:
              <span
                style={{
                  fontSize: '0.65rem',
                  color: '#D81939',
                  textAlign: 'center',
                }}
              >
                {' '}
                ({tkofOATMaxMin.min} - {tkofOATMaxMin.max})
              </span>
            </label>
            <input
              {...register('OAT', {
                required: 'This field is required',
                min: {
                  value: tkofOATMaxMin.min,
                  message: 'Minimum ' + tkofOATMaxMin.min,
                },
                max: {
                  value: tkofOATMaxMin.max,
                  message: 'Maximum ' + tkofOATMaxMin.max,
                },
                pattern: {
                  value: /^-?\d+$/,
                  message: 'Not a valid input',
                },
              })}
              type='number'
            />
          </div>
          {errors.OAT && <p className='error_text'>{errors.OAT?.message}</p>}

          <div className='input-row'>
            <label htmlFor='QNH'>
              QNH:
              <span
                style={{
                  fontSize: '0.65rem',
                  color: '#D81939',
                  textAlign: 'center',
                }}
              >
                {' '}
                ({tkofQNHMaxMin.min} - {tkofQNHMaxMin.max})
              </span>
            </label>
            <input
              {...register('QNH', {
                required: 'This field is required',
                min: {
                  value: tkofQNHMaxMin.min,
                  message: 'Minimum ' + tkofQNHMaxMin.min,
                },
                max: {
                  value: tkofQNHMaxMin.max,
                  message: 'Maximum ' + tkofQNHMaxMin.max,
                },
              })}
              type='number'
            />
          </div>
          {errors.QNH && <p className='error_text'>{errors.QNH?.message}</p>}

          <button type='submit' value='Submit' className='calc-button'>
            Calculate
          </button>
        </form>
      </>
    );
  };

  const ldgForm = () => {
    if (!ldgSettings) return <p> Loading Settings... </p>;

    if (destAirportICAO === null || destAirportICAO === undefined)
      return <p> Loading Settings... </p>;

    // If ldgSettings is not an object, it will be a string as there will have
    // an error in the fetch to the webservice of some kind or the given flight does not
    // have any settings attached to it
    if (typeof ldgSettings !== 'object') return ldgSettings;

    const handleDestICAOChange = (e) => {
      setDestAirportICAO(e.target.value.toUpperCase());
      populateRWYOptions(destAirportICAO, ldgSettings);
    };

    const ldgWindDirMaxMin = getLDGMaxMin(ldgSettings, 'WindDir');
    const ldgWindSpeedMaxMin = getLDGMaxMin(ldgSettings, 'WindSpeed');
    const ldgOATMaxMin = getLDGMaxMin(ldgSettings, 'OAT');
    const ldgQNHMaxMin = getLDGMaxMin(ldgSettings, 'QNH');
    const ldgLWMaxMin = getLDGMaxMin(ldgSettings, 'ALM');
    const ldgTHRMaxMin = getLDGMaxMin(ldgSettings, 'ThrSpeedInc');
    const ldgAPPMaxMin = getLDGMaxMin(ldgSettings, 'AppClbGrad');

    return (
      <>
        <form onSubmit={handleSubmit(handleLdgCalc)} className='form'>
          <div className='input-row'>
            <span>ACReg: </span>
            <span>{props.data?.sACReg}</span>
          </div>

          <div className='input-row'>
            <label htmlFor='ldgICAO'>ICAO:</label>
            <input
              {...register('ldgICAO', {
                required: 'This field is required',
                minLength: {
                  value: 4,
                  message: 'ICAO code is 4 letters',
                },
                pattern: {
                  value: /^[A-Z]+$/i,
                  message: 'Invalid ICAO code',
                },
              })}
              list='icao'
              style={{ textTransform: 'uppercase' }}
              maxLength={4}
              value={destAirportICAO}
              onChange={(e) => {
                handleDestICAOChange(e);
                reset();
              }}
            />
            <datalist id='icao'>
              {getICAOList(ldgSettings).map((icao, index) => {
                return (
                  <option key={'ldgICAO' + index} value={icao}>
                    {icao}
                  </option>
                );
              })}
            </datalist>
          </div>
          {errors.ldgICAO && (
            <p className='error_text'>{errors.ldgICAO?.message}</p>
          )}

          <div className='input-row'>
            <label htmlFor='ldgRWY'>RWY:</label>
            <select
              {...register('ldgRWY', { required: 'This field is required' })}
            >
              {populateRWYOptions(destAirportICAO, ldgSettings, 'LDG')}
            </select>
          </div>
          {errors.ldgRWY && (
            <p className='error_text'>{errors.ldgRWY?.message}</p>
          )}

          {optionListComponent(
            ldgSettings,
            'GrSpoiler',
            'GR SPOILER:',
            'ldgGrSpoiler',
            getLDGOptionList
          )}
          {errors.ldgGrSpoiler && (
            <p className='error_text'>{errors.ldgGrSpoiler?.message}</p>
          )}

          {optionListComponent(
            ldgSettings,
            'AntiSkid',
            'ANTISKID:',
            'ldgAntiSkid',
            getLDGOptionList
          )}
          {errors.ldgAntiSkid && (
            <p className='error_text'>{errors.ldgAntiSkid?.message}</p>
          )}

          {optionListComponent(
            ldgSettings,
            'ThrRev',
            'THR REV:',
            'ldgThrRev',
            getLDGOptionList
          )}
          {errors.ldgThrRev && (
            <p className='error_text'>{errors.ldgThrRev?.message}</p>
          )}

          {optionListComponent(
            ldgSettings,
            'LandGear',
            'LAND GEAR:',
            'ldgLandGear',
            getLDGOptionList
          )}
          {errors.ldgLandGear && (
            <p className='error_text'>{errors.ldgLandGear?.message}</p>
          )}

          {optionListComponent(
            ldgSettings,
            'PowerManSys',
            'PWR MAN SYS:',
            'ldgPowerManSys',
            getLDGOptionList
          )}
          {errors.ldgPowerManSys && (
            <p className='error_text'>{errors.ldgPowerManSys?.message}</p>
          )}

          {optionListComponent(
            ldgSettings,
            'Flaps',
            'FLAPS:',
            'ldgFlaps',
            getLDGOptionList
          )}
          {errors.ldgFlaps && (
            <p className='error_text'>{errors.ldgFlaps?.message}</p>
          )}

          {optionListComponent(
            ldgSettings,
            'RwyCond',
            'RWY COND:',
            'ldgRWYCond',
            getLDGOptionList
          )}
          {errors.ldgRWYCond && (
            <p className='error_text'>{errors.ldgRWYCond?.message}</p>
          )}

          {watch('ldgRWYCond') === 'DEGRADED BRAKING ACTION' && (
            <>
              {optionListComponent(
                ldgSettings,
                'BA',
                'BA:',
                'ldgBA',
                getLDGOptionList
              )}
              {errors.ldgBA && (
                <p className='error_text'>{errors.ldgBA?.message}</p>
              )}

            </>
          )}

          {optionListComponent(
            ldgSettings,
            'NonNormal',
            'NON NORMAL:',
            'ldgNonNormal',
            getLDGOptionList
          )}
          {errors.ldgNonNormal && (
            <p className='error_text'>{errors.ldgNonNormal?.message}</p>
          )}

          <br></br>

          <p className='metar_taf_title'>METAR</p>
          <p className='metar_taf_raw_text'>
            {ldgMetar?.raw_text ?? 'METAR data not found'}
          </p>

          <br></br>

          <p className='metar_taf_title'>TAF</p>
          <p className='metar_taf_raw_text'>
            {ldgTaf?.rawTAF
              ? formatTafToHTML(ldgTaf.rawTAF, destAirportICAO)
              : 'TAF data not found'}
          </p>

          <br></br>

          <div className='input-row'>
            <label htmlFor='ldgWindDir'>
              WIND DIR:
              <span
                style={{
                  fontSize: '0.65rem',
                  color: '#D81939',
                  textAlign: 'center',
                }}
              >
                {' '}
                ({ldgWindDirMaxMin.min} - {ldgWindDirMaxMin.max})
              </span>
            </label>
            <input
              {...register('ldgWindDir', {
                required: 'This field is required',
                min: {
                  value: ldgWindDirMaxMin.min,
                  message: 'Minimum ' + ldgWindDirMaxMin.min,
                },
                max: {
                  value: ldgWindDirMaxMin.max,
                  message: 'Maximum ' + ldgWindDirMaxMin.max,
                },
              })}
              value={ldgWindDir}
              onChange={(e) => setLdgWindDir(e.target.value)}
              type='number'
            />
          </div>
          {errors.ldgWindDir && (
            <p className='error_text'>{errors.ldgWindDir?.message}</p>
          )}

          <div className='input-row'>
            <label htmlFor='ldgWindSpeed'>
              WIND SPD:
              <span
                style={{
                  fontSize: '0.65rem',
                  color: '#D81939',
                  textAlign: 'center',
                }}
              >
                {' '}
                ({ldgWindSpeedMaxMin.min} - {ldgWindSpeedMaxMin.max})
              </span>
            </label>
            <input
              {...register('ldgWindSpeed', {
                required: 'This field is required',
                min: {
                  value: ldgWindSpeedMaxMin.min,
                  message: 'Minimum ' + ldgWindSpeedMaxMin.min,
                },
                max: {
                  value: 40,
                  message: 'Maximum ' + ldgWindSpeedMaxMin.max,
                },
                pattern: {
                  value: /^-?\d+$/,
                  message: 'Not a valid input',
                },
              })}
              value={ldgWindSpeed}
              onChange={(e) => setLdgWindSpeed(e.target.value)}
              type='number'
            />
          </div>
          {errors.ldgWindSpeed && (
            <p className='error_text'>{errors.ldgWindSpeed?.message}</p>
          )}

          <div className='input-row'>
            <label htmlFor='ldgOAT'>
              OAT:
              <span
                style={{
                  fontSize: '0.65rem',
                  color: '#D81939',
                  textAlign: 'center',
                }}
              >
                {' '}
                ({ldgOATMaxMin.min} - {ldgOATMaxMin.max})
              </span>
            </label>
            <input
              {...register('ldgOAT', {
                required: 'This field is required',
                min: {
                  value: ldgOATMaxMin.min,
                  message: 'Minimum ' + ldgOATMaxMin.min,
                },
                max: {
                  value: ldgOATMaxMin.max,
                  message: 'Maximum ' + ldgOATMaxMin.max,
                },
                pattern: {
                  value: /^-?\d+$/,
                  message: 'Not a valid input',
                },
              })}
              value={ldgOAT}
              onChange={(e) => setLdgOAT(e.target.value)}
              type='number'
            />
          </div>
          {errors.ldgOAT && (
            <p className='error_text'>{errors.ldgOAT?.message}</p>
          )}

          <div className='input-row'>
            <label htmlFor='ldgQNH'>
              QNH:
              <span
                style={{
                  fontSize: '0.65rem',
                  color: '#D81939',
                  textAlign: 'center',
                }}
              >
                {' '}
                ({ldgQNHMaxMin.min} - {ldgQNHMaxMin.max})
              </span>
            </label>
            <input
              {...register('ldgQNH', {
                required: 'This field is required',
                min: {
                  value: ldgQNHMaxMin.min,
                  message: 'Minimum ' + ldgQNHMaxMin.min,
                },
                max: {
                  value: ldgQNHMaxMin.max,
                  message: 'Maximum ' + ldgQNHMaxMin.max,
                },
              })}
              value={ldgQNH}
              onChange={(e) => setLdgQNH(e.target.value)}
              type='number'
            />
          </div>
          {errors.ldgQNH && (
            <p className='error_text'>{errors.ldgQNH?.message}</p>
          )}

          <div className='input-row'>
            <label htmlFor='ldgLW'>
              LW:
              <span
                style={{
                  fontSize: '0.65rem',
                  color: '#D81939',
                  textAlign: 'center',
                }}
              >
                {' '}
                ({ldgLWMaxMin.min} - {ldgLWMaxMin.max})
              </span>
            </label>
            <input
              {...register('ldgLW', {
                required: 'This field is required',
                min: {
                  value: ldgLWMaxMin.min,
                  message: 'Minimum ' + ldgLWMaxMin.min,
                },
                max: {
                  value: ldgLWMaxMin.max,
                  message: 'Maximum ' + ldgLWMaxMin.max,
                },
              })}
              value={ldgLW}
              onChange={(e) => setLdgLW(e.target.value)}
              type='number'
            />
          </div>
          {errors.ldgLW && (
            <p className='error_text'>{errors.ldgLW?.message}</p>
          )}

          <div className='input-row'>
            <label htmlFor='ldgThrSpeedInc'>
              THR SPEED INC:
              <span
                style={{
                  fontSize: '0.65rem',
                  color: '#D81939',
                  textAlign: 'center',
                }}
              >
                {' '}
                ({ldgTHRMaxMin.min} - {ldgTHRMaxMin.max})
              </span>
            </label>
            <input
              {...register('ldgThrSpeedInc', {
                required: 'This field is required',
                min: {
                  value: ldgTHRMaxMin.min,
                  message: 'Minimum ' + ldgTHRMaxMin.min,
                },
                max: {
                  value: ldgTHRMaxMin.max,
                  message: 'Maximum ' + ldgTHRMaxMin.max,
                },
              })}
              value={ldgThrSpeedInc}
              onChange={(e) => setLdgThrSpeedInc(e.target.value)}
              type='number'
            />
          </div>
          {errors.ldgThrSpeedInc && (
            <p className='error_text'>{errors.ldgThrSpeedInc?.message}</p>
          )}

          <div className='input-row'>
            <label htmlFor='ldgAppClbGrad'>
              APP CLB GRAD:
              <span
                style={{
                  fontSize: '0.65rem',
                  color: '#D81939',
                  textAlign: 'center',
                }}
              >
                {' '}
                ({ldgAPPMaxMin.min} - {ldgAPPMaxMin.max})
              </span>
            </label>
            <input
              {...register('ldgAppClbGrad', {
                required: 'This field is required',
                min: {
                  value: ldgAPPMaxMin.min,
                  message: 'Minimum ' + ldgAPPMaxMin.min,
                },
                max: {
                  value: ldgAPPMaxMin.max,
                  message: 'Maximum ' + ldgAPPMaxMin.max,
                },
                pattern: {
                  value: /^(?=.+)(?:[1-9]\d*|0)?(?:\.\d+)?$/,
                  message: 'Not a valid input',
                },
              })}
              value={ldgAppClbGrad}
              onChange={(e) => setLdgAppClbGrad(e.target.value)}
              type='number'
            />
          </div>
          {errors.ldgAppClbGrad && (
            <p className='error_text'>{errors.ldgAppClbGrad?.message}</p>
          )}

          <button type='submit' value='Submit' className='calc-button'>
            Calculate
          </button>
        </form>
      </>
    );
  };

  return (
    <div
      className={'content-container'}
      style={{ alignItems: 'center', justifyContent: 'content' }}
    >
      <h3 style={{ marginTop: 0, marginBottom: 5 }}>PERFORMANCE CALCULATION</h3>
      <span
        style={{
          color: 'red',
          fontSize: 10,
          textAlign: 'center',
          fontWeight: 'bold',
          marginBottom: 5,
        }}
      >
        This page requires an internet connection
      </span>

      <div style={{ display: 'flex', flexDirection: 'row', marginBottom: 10 }}>
        <div
          onClick={toggleCalcMode}
          style={{ marginRight: 30, cursor: 'pointer' }}
        >
          <label>
            <input type='radio' checked={tkofMode} onChange={() => null} /> TKOF
          </label>
        </div>
        <div
          onClick={toggleCalcMode}
          style={{ marginLeft: 30, cursor: 'pointer' }}
        >
          <label>
            <input type='radio' checked={!tkofMode} onChange={() => null} /> LDG
          </label>
        </div>
      </div>

      <>{tkofMode ? <>{tkofForm()}</> : <>{ldgForm()}</>}</>

      {openResultPopup && (
        <>
          <div
            className='overlay-calc'
            onClick={() => {
              setOpenResultPopup((prevState) => !prevState);
              setCalculationResult('');
            }}
          ></div>
          <div className='result-popup'>
            <div
              className='close-row-calc'
              onClick={() => {
                setOpenResultPopup((prevState) => !prevState);
                setCalculationResult('');
              }}
            >
              <CloseIcon
                fontSize='medium'
                sx={[{ '&hover': { cursor: 'pointer' } }]}
              />
            </div>
            {tkofMode ? (
              <p style={{ textAlign: 'center' }}>OUTPUT TKOF CALCULATION</p>
            ) : (
              <p style={{ textAlign: 'center' }}>OUTPUT LDG CALCULATION</p>
            )}
            {calculationResult === '' ? (
              <span style={{ textAlign: 'center', marginTop: '10px' }}>
                {' '}
                Loading output...
              </span>
            ) : (
              <pre className='result-text' style={{ marginTop: '10px' }}>
                {calculationResult}
              </pre>
            )}
          </div>
        </>
      )}
    </div>
  );
}
